import api from '../../../api';
import { ResponseSuccess } from '../../../shared/types';
import {
  DateRangeFilter,
  EnrichmentStatus,
  EnrichmentType,
} from '../enums/csv-enrichment.enums';
import { CreateEnrichmentPayload } from '../types/csv-enrichment.types';
import { getPendingCsvFile, setPendingCsvFile } from './csv-enrichment.helpers';

export const getEnrichmentJobsApi = async (filters: {
  dateRange: DateRangeFilter;
  search: string;
  status: EnrichmentStatus | null;
  type: EnrichmentType | null;
  page: number;
  pageSize: number;
  sortBy?: string | null;
  sortOrder?: string | null;
}) => {
  const params: Record<string, string | number> = {
    page: filters.page,
    pageSize: filters.pageSize,
  };
  if (filters.search) params.search = filters.search;
  if (filters.status) params.status = filters.status;
  if (filters.type) params.type = filters.type;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;

  const response = await api.get<ResponseSuccess>('/csv-enrichment/jobs', {
    params,
  });
  return response;
};

export const getEnrichmentStatsApi = async () => {
  const response = await api.get<ResponseSuccess>('/csv-enrichment/stats');
  return response;
};

export const createEnrichmentJobApi = async (
  payload: CreateEnrichmentPayload,
) => {
  const file = getPendingCsvFile();
  if (!file) {
    throw new Error('No CSV file selected');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', payload.fileName);
  formData.append('totalRecords', String(payload.totalRecords));
  formData.append('component', payload.component);
  formData.append('revealType', payload.revealType);
  formData.append('columnMapping', JSON.stringify(payload.columnMapping));
  const exportColumns = Array.from(
    new Set(
      (payload.exportColumns ?? []).filter(
        (key) => typeof key === 'string' && key.trim().length > 0,
      ),
    ),
  );
  formData.append('exportColumns', JSON.stringify(exportColumns));

  const response = await api.post<ResponseSuccess>(
    '/csv-enrichment/jobs',
    formData,
  );

  // Clear the file reference after successful upload
  setPendingCsvFile(null);

  return response;
};

export const deleteEnrichmentJobApi = async (id: string) => {
  const response = await api.delete<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}`,
  );
  return response;
};

export const getEnrichmentJobStatusApi = async (id: string) => {
  const response = await api.get<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}/status`,
  );
  return response;
};

export const getEnrichmentJobRecordsApi = async (
  id: string,
  page = 1,
  pageSize = 25,
) => {
  const response = await api.get<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}/records`,
    { params: { page, pageSize } },
  );
  return response;
};

export const exportEnrichmentJobApi = async (id: string) => {
  const response = await api.post<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}/export`,
    {},
  );

  // If a download URL is returned, trigger the download
  const downloadUrl = (response as any)?.payload?.downloadUrl;
  if (downloadUrl) {
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = '';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }

  return response;
};

export const failedExportJobApi = async (id: string) => {
  const response = await api.post<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}/failed-export`,
    {},
  );

  const downloadUrl = (response as any)?.payload?.downloadUrl;
  if (downloadUrl) {
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = '';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }

  return response;
};

export const cancelEnrichmentJobApi = async (id: string) => {
  const response = await api.patch<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}`,
    {},
  );
  return response;
};

export const addJobToProspectsApi = async (id: string) => {
  const response = await api.post<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}/add-to-prospects`,
    {},
  );
  return response;
};

export const addJobToSequenceApi = async (
  id: string,
  sequenceId: number,
  stepId: number,
) => {
  const response = await api.post<ResponseSuccess>(
    `/csv-enrichment/jobs/${id}/add-to-sequence`,
    { sequenceId, stepId },
  );
  return response;
};
